import {
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';

import { WindowValues } from '@shared/components/table/table-cell/utils';

export interface TimeValue {
  hours: number;
  minutes: number;
}

export type TimeCellEditorValue = TimeValue | WindowValues<TimeValue> | undefined;

@Component({
  selector: 'app-timepicker-dropdown',
  standalone: true,
  imports: [],
  templateUrl: './timepicker-dropdown.component.html'
})
export class TimepickerDropdownComponent implements OnInit {
  window = input<boolean>(false);
  actualValue = input<TimeCellEditorValue>();

  selectedValue = signal<TimeCellEditorValue>(this.actualValue());
  selectedInputIndex = signal<number>(0);

  constructor() {
    effect(() => {
      // Watch for selectedValue changes and refocus
      this.selectedValue(); // Trigger reactivity

      // Use untracked to avoid infinite loops since focus() doesn't change signals
      untracked(() => {
        this.focus();
      });
    });
  }

  // ViewChild reference to the first input element
  private firstInput = viewChild<ElementRef<HTMLInputElement>>('firstInput');

  // New state for tracking which pointer is being dragged
  protected draggingPointerType = signal<'hour' | 'minute' | null>(null);
  protected draggingHourIndex = signal<number | null>(null);
  protected draggingMinuteIndex = signal<number | null>(null);

  updatedValue = output<TimeCellEditorValue>();

  protected readonly range = computed(() =>
    Array.from({ length: this.window() ? 2 : 1 }, (_, i) => i)
  );

  // Generate hours (0-23)
  protected readonly hours = Array.from({ length: 24 }, (_, i) => i);

  // Generate minutes (0, 5, 10, 15, ..., 55) - labels only
  protected readonly minuteLabels = Array.from({ length: 12 }, (_, i) => i * 5);

  // Outer circle properties (hours)
  protected readonly radius = 100;
  protected readonly circumference = 2 * Math.PI * this.radius;

  // Inner circle properties (minutes)
  protected readonly innerRadius = 60;
  protected readonly innerCircumference = 2 * Math.PI * this.innerRadius;

  protected circleStartPercent = signal<number>(0);
  protected circleEndPercent = signal<number>(0);

  /** Public method to focus the first input element in the dropdown */
  public focus(): void {
    this.firstInput()?.nativeElement.focus();
  }

  ngOnInit() {
    if (!this.actualValue()) {
      const now = new Date();
      const currentTime: TimeValue = {
        hours: now.getHours(),
        minutes: now.getMinutes()
      };

      if (this.window()) {
        this.selectedValue.set({ start: currentTime, finish: currentTime });
      } else {
        this.selectedValue.set(currentTime);
      }
    } else {
      this.selectedValue.set(this.actualValue());
    }

    this.updateCircleDisplay();
  }

  protected formatTime(time: TimeValue): string {
    return `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}`;
  }

  protected getInputValue(key: number): string {
    const value = this.selectedValue();

    if (this.window() && value && 'start' in value && 'finish' in value) {
      const val = value as WindowValues<TimeValue>;
      return this.formatTime(key === 0 ? val.start : val.finish);
    } else if (value && key === 0) {
      return this.formatTime(value as TimeValue);
    }

    return '00:00';
  }

  protected onInputChange(event: Event, key: number) {
    const input = event.target as HTMLInputElement;
    const timeParts = input.value.split(':');

    if (timeParts.length !== 2) return;

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return;
    }

    const newTime: TimeValue = { hours, minutes };
    const currentValue = this.selectedValue();

    // A focusout that changes nothing has to stay silent. Republishing an equal time still
    // notifies `selectedValue`, whose effect puts focus back on this input; while a second
    // picker is mounted — the dropdown keeps a closing panel through its fade-out — the two
    // take focus from each other, and every steal is another focusout. Change detection
    // never settles and Angular gives up with NG0103.
    if (currentValue) {
      const existing = this.getTimeForIndex(key);
      if (existing.hours === hours && existing.minutes === minutes) {
        // The `[value]` binding does not re-run while the signal is untouched, so a
        // non-canonical spelling of the same time ("8:0") would otherwise stay on screen.
        input.value = this.formatTime(existing);
        return;
      }
    }

    if (
      this.window() &&
      currentValue &&
      'start' in currentValue &&
      'finish' in currentValue
    ) {
      const val = currentValue as WindowValues<TimeValue>;
      const updated = key === 0 ? [newTime, val.finish] : [val.start, newTime];

      const startMinutes = updated[0].hours * 60 + updated[0].minutes;
      const endMinutes = updated[1].hours * 60 + updated[1].minutes;

      if (startMinutes >= endMinutes) {
        return;
      }

      this.selectedValue.set({ start: updated[0], finish: updated[1] });
    } else {
      this.selectedValue.set(newTime);
    }

    this.updatedValue.emit(this.selectedValue());
    this.updateCircleDisplay();
  }

  protected onSelectPart(key: number) {
    if (this.window()) {
      this.selectedInputIndex.set(key);
    }
  }

  protected getClassesForPart(key: number): string {
    const base = 'flex items-center rounded-md grow px-2 bg-zinc-100 dark:bg-zinc-800 h-8';

    if (!this.window()) return base;

    if (this.selectedInputIndex() === key) {
      return `${base} border-2 border-blue-500`;
    }

    return base;
  }

  protected onKeyInput(e: KeyboardEvent) {
    // Only stop propagation for arrow keys - they should navigate within the picker
    // Allow Enter, Escape, Tab to propagate to parent for commit/cancel logic
    const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (arrowKeys.includes(e.key)) {
      e.stopPropagation();
    }
  }

  /**
   * Handle mouse down on hour pointer
   *
   * @param pointIndex 0 for start point, 1 for end point
   */
  protected onHourPointerMouseDown(pointIndex: number) {
    this.draggingPointerType.set('hour');
    this.draggingHourIndex.set(pointIndex);
    if (this.window()) {
      this.selectedInputIndex.set(pointIndex);
    }
  }

  /**
   * Handle mouse down on minute pointer
   *
   * @param pointIndex 0 for start point, 1 for end point
   */
  protected onMinutePointerMouseDown(pointIndex: number) {
    this.draggingPointerType.set('minute');
    this.draggingMinuteIndex.set(pointIndex);
    if (this.window()) {
      this.selectedInputIndex.set(pointIndex);
    }
  }

  /** Handle mouse up to stop dragging */
  protected onSvgMouseUp() {
    this.draggingPointerType.set(null);
    this.draggingHourIndex.set(null);
    this.draggingMinuteIndex.set(null);
  }

  /**
   * Handle mouse move on SVG to update point position while dragging
   *
   * @param event Mouse event
   */
  protected onSvgMouseMove(event: MouseEvent) {
    const pointerType = this.draggingPointerType();
    if (!pointerType) return;

    if (pointerType === 'hour') {
      this.handleHourDrag(event);
    } else {
      this.handleMinuteDrag(event);
    }
  }

  /**
   * Handle hour pointer dragging on outer circle
   *
   * @param event Mouse event
   */
  protected handleHourDrag(event: MouseEvent) {
    const draggingIndex = this.draggingHourIndex();
    if (draggingIndex === null) return;

    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();

    const mouseX = event.clientX - rect.left - 130;
    const mouseY = event.clientY - rect.top - 130;

    let angle = Math.atan2(mouseY, mouseX) + Math.PI / 2;
    if (angle < 0) angle += 2 * Math.PI;

    const hours = Math.round((angle / (2 * Math.PI)) * 24) % 24;

    this.updateTime(draggingIndex, { hours, minutes: null });
  }

  /**
   * Handle minute pointer dragging on inner circle
   *
   * @param event Mouse event
   */
  protected handleMinuteDrag(event: MouseEvent) {
    const draggingIndex = this.draggingMinuteIndex();
    if (draggingIndex === null) return;

    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();

    const mouseX = event.clientX - rect.left - 130;
    const mouseY = event.clientY - rect.top - 130;

    let angle = Math.atan2(mouseY, mouseX) + Math.PI / 2;
    if (angle < 0) angle += 2 * Math.PI;

    const minutes = Math.round((angle / (2 * Math.PI)) * 60) % 60;

    this.updateTime(draggingIndex, { hours: null, minutes });
  }

  /**
   * Update time values (hours or minutes or both)
   *
   * @param index 0 for start/single, 1 for finish
   * @param changes Object with hours and/or minutes to update (null means keep existing)
   */
  protected updateTime(
    index: number,
    changes: { hours: number | null; minutes: number | null }
  ) {
    const currentValue = this.selectedValue();

    if (
      this.window() &&
      currentValue &&
      'start' in currentValue &&
      'finish' in currentValue
    ) {
      const val = currentValue as WindowValues<TimeValue>;
      const updated = [val.start, val.finish];

      // Update only the specified fields, preserve others
      updated[index] = {
        hours: changes.hours !== null ? changes.hours : updated[index].hours,
        minutes: changes.minutes !== null ? changes.minutes : updated[index].minutes
      };

      const startMinutes = updated[0].hours * 60 + updated[0].minutes;
      const endMinutes = updated[1].hours * 60 + updated[1].minutes;

      if (index === 0 && startMinutes >= endMinutes) {
        return;
      }
      if (index === 1 && endMinutes <= startMinutes) {
        return;
      }

      this.selectedValue.set({ start: updated[0], finish: updated[1] });
      this.updatedValue.emit(this.selectedValue());
      this.updateCircleDisplay();
    } else if (!this.window() && currentValue) {
      const val = currentValue as TimeValue;
      this.selectedValue.set({
        hours: changes.hours !== null ? changes.hours : val.hours,
        minutes: changes.minutes !== null ? changes.minutes : val.minutes
      });
      this.updatedValue.emit(this.selectedValue());
      this.updateCircleDisplay();
    }
  }

  /**
   * Handle click on an hour number Updates the selected input with the clicked hour value
   *
   * @param hour The clicked hour (0-23)
   */
  protected onHourClick(hour: number) {
    const currentValue = this.selectedValue();

    if (
      this.window() &&
      currentValue &&
      'start' in currentValue &&
      'finish' in currentValue
    ) {
      let index = this.selectedInputIndex();
      const val = currentValue as WindowValues<TimeValue>;

      if (
        (index === 0 && hour > val.finish.hours) ||
        (hour === val.finish.hours && val.start.minutes >= val.start.minutes)
      ) {
        this.selectedInputIndex.set(1);
        index = 1;
      } else if (
        (index === 1 && hour < val.start.hours) ||
        (hour === val.start.hours && val.finish.minutes <= val.start.minutes)
      ) {
        this.selectedInputIndex.set(0);
        index = 0;
      }

      const updatedValue: WindowValues<TimeValue> = {
        start: index === 0 ? { hours: hour, minutes: val.start.minutes } : val.start,
        finish: index === 1 ? { hours: hour, minutes: val.finish.minutes } : val.finish
      };
      this.selectedValue.set(updatedValue);

      this.selectedInputIndex.set(index === 0 ? 1 : 0);
    } else if (currentValue && !Array.isArray(currentValue)) {
      this.selectedValue.set({ ...currentValue, hours: hour });
    }

    this.updatedValue.emit(this.selectedValue());
    this.updateCircleDisplay();
  }

  /**
   * Handle click on a minute marker
   *
   * @param minute The clicked minute (0-59)
   */
  protected onMinuteClick(minute: number) {
    const currentValue = this.selectedValue();

    if (
      this.window() &&
      currentValue &&
      'start' in currentValue &&
      'finish' in currentValue
    ) {
      const index = this.selectedInputIndex();
      const val = currentValue as WindowValues<TimeValue>;

      const updatedValue: WindowValues<TimeValue> = {
        start: index === 0 ? { ...val.start, minutes: minute } : val.start,
        finish: index === 1 ? { ...val.finish, minutes: minute } : val.finish
      };

      // Validate start < finish
      const startMinutes = updatedValue.start.hours * 60 + updatedValue.start.minutes;
      const finishMinutes = updatedValue.finish.hours * 60 + updatedValue.finish.minutes;

      if (startMinutes >= finishMinutes) {
        return;
      }

      this.selectedValue.set(updatedValue);
    } else if (currentValue) {
      const val = currentValue as TimeValue;
      this.selectedValue.set({ ...val, minutes: minute });
    }

    this.updatedValue.emit(this.selectedValue());
    this.updateCircleDisplay();
  }

  /** Update the circle display based on current selected values */
  protected updateCircleDisplay() {
    const currentValue = this.selectedValue();
    if (!currentValue) return;

    if (
      this.window() &&
      currentValue &&
      'start' in currentValue &&
      'finish' in currentValue
    ) {
      const start = currentValue.start;
      const end = currentValue.finish;

      const startMinutes = start.hours * 60 + start.minutes;
      const endMinutes = end.hours * 60 + end.minutes;
      const startPercent = (startMinutes / 1440) * 100;
      const endPercent = (endMinutes / 1440) * 100;

      this.circleStartPercent.set(startPercent);
      this.circleEndPercent.set(endPercent);
    } else if (currentValue && !this.window()) {
      const val = currentValue as TimeValue;
      const totalMinutes = val.hours * 60 + val.minutes;
      const currentPercent = (totalMinutes / 1440) * 100;
      this.circleStartPercent.set(currentPercent);
      this.circleEndPercent.set(currentPercent);
    }
  }

  /**
   * Calculate stroke-dasharray to create the arc between start and end percentages
   *
   * @returns The stroke-dasharray value
   */
  protected getStrokeDasharray(): string {
    const startPercent = this.circleStartPercent();
    const endPercent = this.circleEndPercent();
    const arcLength = ((endPercent - startPercent) / 100) * this.circumference;
    return `${arcLength} ${this.circumference}`;
  }

  /**
   * Calculate stroke-dashoffset to position the arc at the correct starting point
   *
   * @returns The stroke-dashoffset value
   */
  protected getStrokeDashoffset(): number {
    const startPercent = this.circleStartPercent();
    return -1 * (startPercent / 100) * this.circumference;
  }

  /**
   * Calculate the position of an hour number on the circle
   *
   * @param hour Hour value (0-23)
   * @returns Object with x and y coordinates
   */
  protected getHourPosition(hour: number): { x: number; y: number } {
    const angle = (hour / 24) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 130 + this.radius * Math.cos(angle),
      y: 130 + this.radius * Math.sin(angle)
    };
  }

  /**
   * Calculate the position of the start point on the circle
   *
   * @returns Object with x and y coordinates
   */
  protected getStartPoint(): { x: number; y: number } {
    const startPercent = this.circleStartPercent();
    const angle = (startPercent / 100) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 130 + this.radius * Math.cos(angle),
      y: 130 + this.radius * Math.sin(angle)
    };
  }

  /**
   * Calculate the position of the end point on the circle
   *
   * @returns Object with x and y coordinates
   */
  protected getEndPoint(): { x: number; y: number } {
    const endPercent = this.circleEndPercent();
    const angle = (endPercent / 100) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 130 + this.radius * Math.cos(angle),
      y: 130 + this.radius * Math.sin(angle)
    };
  }

  /**
   * Get position of a minute marker on inner circle
   *
   * @param minute Minute value (0-59)
   * @returns Object with x and y coordinates
   */
  protected getMinutePosition(minute: number): { x: number; y: number } {
    const angle = (minute / 60) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 130 + this.innerRadius * Math.cos(angle),
      y: 130 + this.innerRadius * Math.sin(angle)
    };
  }

  /**
   * Get position of hour pointer on outer circle
   *
   * @param index 0 for start/single, 1 for finish
   * @returns Object with x and y coordinates
   */
  protected getHourPointerPosition(index: number): { x: number; y: number } {
    const time = this.getTimeForIndex(index);
    const angle = (time.hours / 24) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 130 + this.radius * Math.cos(angle),
      y: 130 + this.radius * Math.sin(angle)
    };
  }

  /**
   * Get position of minute pointer on inner circle
   *
   * @param index 0 for start/single, 1 for finish
   * @returns Object with x and y coordinates
   */
  protected getMinutePointerPosition(index: number): { x: number; y: number } {
    const time = this.getTimeForIndex(index);
    const angle = (time.minutes / 60) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 130 + this.innerRadius * Math.cos(angle),
      y: 130 + this.innerRadius * Math.sin(angle)
    };
  }

  /**
   * Helper to get time value for a given index
   *
   * @param index 0 for start/single, 1 for finish
   * @returns TimeValue object
   */
  protected getTimeForIndex(index: number): TimeValue {
    const value = this.selectedValue();
    if (this.window() && value && 'start' in value) {
      return index === 0 ? value.start : value.finish;
    }
    return (value as TimeValue) || { hours: 0, minutes: 0 };
  }
}
